import moment from 'moment';

import { CampaignStrategyParam } from '../service/StrategyHelpers/StrategyHelpers';

import { DateRangePreset } from './common';
import { CampaignsCalcTypeFilter, GridRow } from './domain';
import { FilterPartialExpression } from './filter';
import {
    CurrencyCode,
    GdCampaignBudgetPeriod,
    GdCampaignPlatform
} from './graphql-grid';
import {
    Age,
    BroadmatchFragment,
    CalcType,
    ChangeSource,
    DemographyMultiplierFragment,
    FlatStrategyFragment,
    GdCampaignStrategyType,
    Gender,
    NetworkFragment,
    OutputCategory,
    RestrictFrequencyFragment,
    RetargetingFragment,
    TimeTargetFragment
} from './graphql-log';
import { SortDirection } from './grid';
import {
    LogCampaignDayBudget,
    LogCampaignStrategyParams,
    StrategyBudgetParamNames,
    StrategyParamNames
} from './strategy';

export interface LogScreenUrlParams {
    datePreset?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    filter?: string | null;
    sortDirection?: SortDirection | null;
    cid?: string | null;
}

export enum LogScreenUrlSearchParams {
    view = 'view',
    datePreset = 'date-preset',
    dateFrom = 'date-from',
    dateTo = 'date-to',
    filter = 'filter',
    cid = 'cid',
    sortDirection = 'sort-direction',
    ulogin = 'ulogin'
}

export enum LogsOrder {
    asc = 'ASC',
    desc = 'DESC' // по умолчанию тоже desc
}

export enum ChangedOptions {
    notModified = 'NOT_MODIFIED',
    modified = 'MODIFIED',
    added = 'ADDED',
    removed = 'REMOVED',
    off = 'OFF'
}

export enum LogTypenames {
    campaignStatus = 'CampaignStatusChangeEvent',
    bannerEvent = 'BannersEvent',
    valueChange = 'CampaignValueChangeEvent',
    timeTargeting = 'CampaignTimeTargetEvent',
    broadmatch = 'CampaignBroadMatchEvent',
    listChange = 'CampaignListChangeEvent',
    network = 'CampaignNetworkEvent',
    regions = 'CampaignRegionsEvent',
    retargeting = 'RetargetingMultipliersEvent',
    demography = 'DemographyMultipliersEvent',
    simpleMultipliers = 'SingleMultiplierEvent',
    strategy = 'CampaignStrategyEvent',
    adgroupRegions = 'AdGroupsRegionsEvent',
    restrictFrequency = 'CampaignRestrictFrequencyEvent'
}

export enum LogEventCategories {
    // фейковые категории: используются только для отображения
    campaignStatus = 'campaign-status',
    bannerStatus = 'banner-status'
}

export type Category = OutputCategory | LogEventCategories;

export enum FiltersForGeneralGroup {
    CAMPAIGN_TIME_TARGET = 'CAMPAIGN_TIME_TARGET',
    REGIONS = 'REGIONS',
    CAMPAIGN_EXTENDED_GEOTARGETING = 'CAMPAIGN_EXTENDED_GEOTARGETING',
    CAMPAIGN_STRATEGY = 'CAMPAIGN_STRATEGY',
    CAMPAIGN_NETWORK = 'CAMPAIGN_NETWORK',
    RETARGETING_MULTIPLIERS = 'RETARGETING_MULTIPLIERS',
    MOBILE_MULTIPLIER = 'MOBILE_MULTIPLIER',
    VIDEO_MULTIPLIER = 'VIDEO_MULTIPLIER',
    DEMOGRAPHY_MULTIPLIERS = 'DEMOGRAPHY_MULTIPLIERS',
    PERFORMANCE_TGO_MULTIPLIER = 'PERFORMANCE_TGO_MULTIPLIER',
    CAMPAIGN_MINUS_WORDS = 'CAMPAIGN_MINUS_WORDS',
    CAMPAIGN_BROAD_MATCH = 'CAMPAIGN_BROAD_MATCH',
    CAMPAIGN_START_TIME = 'CAMPAIGN_START_TIME',
    CAMPAIGN_FINISH_TIME = 'CAMPAIGN_FINISH_TIME',
    CAMPAIGN_DISABLED_DOMAINS = 'CAMPAIGN_DISABLED_DOMAINS',
    CAMPAIGN_DISABLED_SSP = 'CAMPAIGN_DISABLED_SSP',
    CAMPAIGN_DISABLED_IPS = 'CAMPAIGN_DISABLED_IPS',
    CAMPAIGN_RESTRICT_FREQUENCY = 'CAMPAIGN_RESTRICT_FREQUENCY'
}

export enum FiltersForStatusesGroup {
    CAMPAIGN_CREATION = 'CAMPAIGN_CREATION',
    CAMPAIGN_HIDE = 'CAMPAIGN_HIDE',
    CAMPAIGN_SHOW = 'CAMPAIGN_SHOW',
    BANNERS_HIDE = 'BANNERS_HIDE',
    BANNERS_SHOW = 'BANNERS_SHOW',
    CAMPAIGN_ARCHIVED = 'CAMPAIGN_ARCHIVED',
    CAMPAIGN_UNARCHIVED = 'CAMPAIGN_UNARCHIVED',
    BANNERS_ARCHIVED = 'BANNERS_ARCHIVED',
    BANNERS_UNARCHIVED = 'BANNERS_UNARCHIVED'
}

export const allGoals = 0;
export const logBanner = 'logBanner';
export const logGroup = 'logGroup';
export const logCampaign = 'logCampaign';
export type logBanner = typeof logBanner;
export type logGroup = typeof logGroup;
export type logCampaign = typeof logCampaign;

export type NullableString = string | null;
export type NullableNumber = number | null;

export interface TimeTargetCoefficientsDiff {
    oldCoef: number;
    newCoef: number;
}

export interface TimeTargetingDisplay {
    daysText: string;
}

export interface TimeTargetingDayDisplay extends TimeTargetingDisplay {
    hours: Array<TimeTargetingHours>;
}

export interface TimeTargetingDayCorrectionsDisplay
    extends TimeTargetingDisplay {
    corrections: Array<TimeTargetingCorrection>;
}

export interface TimeTargetingHours {
    hoursText: string;
    timeChangedType: ChangedOptions;
}

export interface TimeTargetingCorrection {
    hoursText: string;
    correction: number;
    oldCorrection: number;
    correctionChangedType: ChangedOptions;
}

export interface TimeTargetingHolidays {
    time: Array<TimeTargetingHours>;
    oldCorrection: number;
    newCorrection: number;
    correctionChangeType: ChangedOptions;
}

export interface TimeTargetGroups extends DisplayGroups {
    timezone: { id: NullableString; name: NullableString };
    oldTimezone: { id: NullableString; name: NullableString };
    time: Array<TimeTargetingDayDisplay>;
    corrections: Array<TimeTargetingDayCorrectionsDisplay>;
    workingWeekends: {
        oldWW: boolean;
        newWW: boolean;
    };
    holidays: TimeTargetingHolidays;
}

export interface RegionsGroups extends DisplayGroups {
    before: string;
    after: string;
}

export interface BroadmatchGroups extends DisplayGroups {
    before: BroadmatchValue;
    after: BroadmatchValue;
}

export interface InlineDisplayGroups extends DisplayGroups {
    [ChangedOptions.added]: Array<number | string>;
    [ChangedOptions.removed]: Array<number | string>;
    [ChangedOptions.notModified]: Array<number | string>;
    [ChangedOptions.off]: Array<number | string>;
    noInsideComma?: boolean;
}

export interface NetworkGroups extends DisplayGroups {
    before: NetworkFragment;
    after: NetworkFragment;
}

export interface RestrictFrequencyGroups extends DisplayGroups {
    before: RestrictFrequencyFragment;
    after: RestrictFrequencyFragment;
}

export interface MultipliersGroups extends DisplayGroups {
    value: Array<MultiplierValue>;
}

export interface SimpleMultiplierGroups extends DisplayGroups {
    value: number;
    compareValue: number;
    type: ChangedOptions;
}

export interface StrategyGroups extends DisplayGroups {
    name: {
        oldName?: GdCampaignStrategyType;
        newName: GdCampaignStrategyType;
        type: ChangedOptions;
    };
    platform: {
        oldPlatform?: GdCampaignPlatform;
        newPlatform: GdCampaignPlatform;
        type: ChangedOptions;
    };
    params: CampaignStrategyParamsDiff;
    budget: CampaignDayBudgetDiff;
}

export interface DisplayGroups {
    needExpand: boolean;
    alwaysNeedExpand: boolean;
    maxLineLength: number;
}

export interface BroadmatchValue extends DiffValue {
    enabled: boolean;
    isLimitDefined: boolean;
    limit: number;
    goal: { id?: number; name: string };
}

export interface SourceMultiplierValue extends DiffValue {
    multiplier: number;
}

export interface MultiplierValue extends SourceMultiplierValue {
    compareMultiplier: number;
    id: string;
    type: ChangedOptions;
}

export interface CampaignStrategyDiffParamValue {
    oldValue: CampaignStrategyParam | GdCampaignBudgetPeriod | null;
    newValue: CampaignStrategyParam | GdCampaignBudgetPeriod | null;
    type: ChangedOptions;
}

export interface CampaignStrategyParamsDiff {
    [StrategyParamNames.goal]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.bid]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.avgBid]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.filterAvgBid]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.avgCpa]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.filterAvgCpa]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.sum]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.clicksLimit]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.profitability]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.roiCoef]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.reserveReturn]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.avgCpi]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.avgCpm]?: CampaignStrategyDiffParamValue;
    [StrategyParamNames.separateBidding]?: CampaignStrategyDiffParamValue;
}

export interface CampaignDayBudgetDiff {
    [StrategyBudgetParamNames.sum]?: CampaignStrategyDiffParamValue;
    [StrategyBudgetParamNames.showMode]?: CampaignStrategyDiffParamValue;
    [StrategyBudgetParamNames.period]?: CampaignStrategyDiffParamValue;
    [StrategyBudgetParamNames.autoProlongation]?: CampaignStrategyDiffParamValue;
    [StrategyBudgetParamNames.start]?: CampaignStrategyDiffParamValue;
    [StrategyBudgetParamNames.finish]?: CampaignStrategyDiffParamValue;
}

export interface StrategyValue extends DiffValue {
    type: GdCampaignStrategyType;
    param: LogCampaignStrategyParams | null;
    platform: GdCampaignPlatform;
    budget: LogCampaignDayBudget | null;
    currency: CurrencyCode;
}

export interface RetargetingValue extends MultiplierValue {
    retargetingConditionId: string;
    retargetingConditionName: string;
}

export interface DemographyMultiplierValue extends MultiplierValue {
    age: Age;
    gender: Gender;
}

export interface DiffValue {}

export interface LogCampaignEntity {
    id: string;
    name: string;
    calcType: CampaignsCalcTypeFilter;
}

export interface LogAdsEntity {
    id: string;
    name: string;
}

export interface LogEntry extends GridRow {
    date: moment.Moment;
    index: number;
    userName?: string;
    campaign: LogCampaignEntity;
    groupsNo: Array<LogAdsEntity>;
    bannersNo: Array<LogAdsEntity>;
    changedFieldId: Category;
    changedValueType: LogTypenames;
    changedValue: DisplayGroups;
    tool: ChangeSource;
    category: OutputCategory;
}

export interface LogPeriod {
    preset?: DateRangePreset;
    dateFrom?: string;
    dateTo?: string;
}

export interface LogSourceParams {
    period: LogPeriod;
    filter?: FilterPartialExpression[];
    order?: SortDirection;
}

export interface LogParams {
    cids?: Array<string>;
    isClient?: boolean;
}

export const defaultLogRowsCount = 200;
export const defaultChangedValueWidth = 470;
export const changedValueKey = 'changedValue';

export interface LogSourceRowEvent {
    __typename: string;
    category: OutputCategory;
    currencyCode?: CurrencyCode;
    campaign: {
        id: string;
        name: string;
        calcType: CalcType;
    };
    adGroup?: {
        id: NullableString;
        name: string;
    };
    adGroups?: Array<{
        id: NullableString;
        name: string;
    } | null>;
    ads?: Array<{
        id: string;
        title: string;
    } | null>;
    oldValue?: NullableString;
    newValue?: NullableString;
    oldList?: Array<NullableString>;
    newList?: Array<NullableString>;
    oldRegions?: Array<NullableNumber>;
    newRegions?: Array<NullableNumber>;
    oldBroadMatch?: BroadmatchFragment;
    newBroadMatch?: BroadmatchFragment;
    oldTimeTarget?: TimeTargetFragment;
    newTimeTarget?: TimeTargetFragment;
    oldNetwork?: NetworkFragment;
    newNetwork?: NetworkFragment;
    oldFlatStrategy?: FlatStrategyFragment | null;
    newFlatStrategy?: FlatStrategyFragment | null;
    oldRetargetingMultipliers?: Array<RetargetingFragment | null>;
    newRetargetingMultipliers?: Array<RetargetingFragment | null>;
    oldDemographyMultipliers?: Array<DemographyMultiplierFragment | null>;
    newDemographyMultipliers?: Array<DemographyMultiplierFragment | null>;
    oldMultiplier?: number;
    newMultiplier?: number;
    oldRestrictFrequency?: RestrictFrequencyFragment;
    newRestrictFrequency?: RestrictFrequencyFragment;
}

export interface LogSourceRow {
    datetime: string;
    user: {
        uid: NullableString;
        login: string;
    } | null;
    changeSource: ChangeSource;
    event: LogSourceRowEvent;
}

export interface LogNavEntityParams {
    cid?: string;
    group?: string;
    banner?: string;
}

export interface LogEntityData {
    id: string;
    name: string;
}
